/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat, Simon Hiller.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import ArticleRating from './ArticleRating';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import extractText from 'flarum/common/utils/extractText';
import Stream from 'flarum/common/utils/Stream';
import TagDiscussionModal from 'flarum/tags/components/TagDiscussionModal';
import getSelectableTags from 'flarum/tags/utils/getSelectableTags';
import Alert from 'flarum/common/components/Alert';
import ArticlePickerModal from 'flarum/orell-articles/components/ArticlePickerModal';
import autobind from 'autobind-decorator';
import { MIN_REVIEW_LENGTH } from '../config';
import { slug } from '../../common';
import Switch from 'flarum/common/components/Switch';
import Tooltip from 'flarum/common/components/Tooltip';
import { utils } from '@glowingblue-orell-thalia-common';
const { Auth } = utils;
import ReviewListState from '../states/ReviewListState';
import Button from 'flarum/common/components/Button';
import { startReviewsTour } from '../extend/reviewsTour';
import ReviewsPage from './ReviewsPage';
import type Mithril from 'mithril';
import type { ComponentAttrs } from 'flarum/common/Component';
import type Discussion from 'flarum/common/models/Discussion';
import type Tag from 'flarum/tags/common/models/Tag';
import type Article from 'flarum/orell-articles/models/Article';
import type { ArticlePickerState } from 'flarum/orell-articles/components/ArticlePickerModal';

export interface ReviewComposerAttrs extends ComponentAttrs {
	/** The article being reviewed. Restored from a draft, in which case only ean + title are set. */
	article?: Article;
	/** Rating carried over from a pre-existing "simple review". */
	rating?: number;
	/** Whether the review should be exported to the Orell Füssli Shop. */
	export?: boolean;
	/** The article page's review list, so the new review can be inserted without a page reload. */
	reviewsState?: ReviewListState;
}

/**
 * `ArticlePickerState` in full — `Partial`, because the composer has none of it until the picker
 * has run once — so reopening the picker restores the previous search. `article` is the composer's
 * own single-selection convenience field, derived from `articles`.
 */
interface ReviewComposerSearchState extends Partial<ArticlePickerState> {
	article?: Article;
}

/** The payload `DiscussionComposer.data()` builds, narrowed to the parts extended here. */
type DiscussionComposerData = Record<string, unknown> & {
	relationships?: Record<string, unknown>;
};

/**
 * DiscussionComposer, composing a review instead. Opened by newReviewAction.
 *
 * CORE_CODE_OVERRIDE
 */
class ReviewComposer extends DiscussionComposer {
	// `ComposerBody` fixes `attrs` to `ComponentAttrs`; re-declared to this composer's attrs type.
	attrs!: ReviewComposerAttrs;

	export: Stream<boolean>;

	/** `false`, or the internal error code that disabled exporting. */
	exportDisabled: Stream<string | false>;

	rating: Stream<number>;

	searchState!: ReviewComposerSearchState;

	oninit(vdom: Mithril.Vnode<ReviewComposerAttrs, this>) {
		super.oninit(vdom);

		// `app.session?.user` is dereferenced unguarded: a session without a user is not a
		// state this composer can be opened in.
		this.export = Stream(app.session?.user!.linkedShop() ? (this.attrs.export ?? app.forum.attribute<boolean>(`${slug}.export_on_create`)) : false);

		this.exportDisabled = Stream<string | false>(false);

		this.rating = Stream(this.attrs.rating ?? 0);

		this.searchState = {};

		if (this.attrs.article) {
			this.searchState.article = this.attrs.article;

			// An article from the attrs may come from a draft, which stores only ean and title.
			if (this.attrs.article.id()) {
				setTimeout(async () => (this.searchState.article = await app.store.find<Article>('of_articles', this.attrs.article!.id()!)));
			}
		} else {
			setTimeout(() => this.manageArticle());
		}

		// Preselect the tag of the page the review was started from, the same way flarum/tags
		// preselects one for a new discussion. Cleared on both sides of the read — the
		// preselection breaks with either reset removed.
		ReviewsPage.prototype.currentActiveTag = undefined;
		const tag = ReviewsPage.prototype.currentTag();
		ReviewsPage.prototype.currentActiveTag = undefined;

		if (tag) {
			const parent = tag.parent();
			this.composer.fields.tags = parent ? [parent, tag] : [tag];
		} else {
			this.composer.fields.tags = [];
		}
	}

	static initAttrs(attrs: ReviewComposerAttrs) {
		super.initAttrs(attrs);

		attrs.placeholder = app.translator.trans('glowingblue-orell-rezensionen.forum.composer_review.body_placeholder');
		attrs.submitLabel = app.translator.trans('glowingblue-orell-rezensionen.forum.composer_review.submit_button');
		// TODO use good string here
		attrs.confirmExit = extractText(app.translator.trans('core.forum.composer_discussion.discard_confirmation'));
		attrs.titlePlaceholder = app.translator.trans('glowingblue-orell-rezensionen.forum.composer_review.title_placeholder');
		attrs.className = 'ComposerBody--review';
	}

	headerItems() {
		const items = super.headerItems();

		const { article } = this.searchState;

		// CORE_CODE_OVERRIDE
		items.setContent('title', <h3>{app.translator.trans('glowingblue-orell-rezensionen.forum.composer_review.title')}</h3>);

		items.remove('polls');

		items.add(
			'article',
			<a className="DiscussionComposer-article" onclick={this.manageArticle}>
				<span className={`ArticleLabel ${article ? '' : 'none'}`}>
					{article ? article.title() : app.translator.trans('glowingblue-orell-rezensionen.forum.composer_review.add_article')}
				</span>
			</a>,
			99
		);

		const exportDisabled = (article && !article?.isAvailable()) || this.exportDisabled();
		let switchState = article ? (article.isAvailable() ? this.export() : false) : this.export();
		if (exportDisabled || !app.session?.user!.linkedShop(false)) {
			switchState = false;
		}

		let exportSwitch = (
			<Switch
				state={switchState}
				onchange={async (value: boolean) => {
					if (!app.session?.user!.linkedShop()) {
						// orell-thalia-common's generated typings declare the optional parameters
						// as `null`, so widen them locally.
						await (Auth.authenticatedCall as (...args: unknown[]) => Promise<unknown>)(
							() => this.export(value),
							app.translator.trans('glowingblue-orell-rezensionen.forum.user.reviews.export_connect'),
							ReviewListState.gaExportAttrs
						);
					}

					this.export(value);
				}}
				// Either a boolean or a truthy error code; `Switch` only tests truthiness.
				disabled={exportDisabled as boolean}
				key="exportSwitch"
			>
				{app.translator.trans('glowingblue-orell-rezensionen.forum.composer_review.export_label')}
			</Switch>
		);

		if (exportDisabled) {
			let tooltipText = app.translator.trans('glowingblue-orell-rezensionen.forum.composer_review.export_disabled_tooltip_unavailable');
			if (this.exportDisabled() === `${slug}:review_locked`) {
				tooltipText = app.translator.trans('glowingblue-orell-rezensionen.forum.composer_review.export_disabled_tooltip_locked');
			}

			exportSwitch = (
				<Tooltip text={tooltipText as string} key={String(this.exportDisabled() || 'unavailable')}>
					{exportSwitch}
				</Tooltip>
			);
		}

		exportSwitch = (
			<div className="ReviewComposer-exportSwitch">
				{exportSwitch}
				<Button
					className="Button Button--icon Button--link hasIcon"
					icon="far fa-question-circle"
					onclick={() => startReviewsTour()}
					key="exportButton"
				/>
			</div>
		);

		items.add('exportSwitch', exportSwitch, 98);

		items.add('reviewRating', <ArticleRating onChange={this.rating} value={this.rating()} />, 97);

		return items;
	}

	manageArticle() {
		const { article, query, results } = this.searchState;
		// Every field of `ArticlePickerState` is required, so anything the composer does not hold
		// yet falls back to the picker's own defaults — `results: false` meaning "not searched yet".
		const state: ArticlePickerState = {
			articles: article ? [article] : [],
			query: query ?? '',
			results: results ?? false,
		};
		// `ArticlePickerModal` types `oninit` against its concrete attrs, which `show()` rejects.
		app.modal.show(ArticlePickerModal as unknown as Parameters<typeof app.modal.show>[0], {
			pickerState: state,
			saveState: this.updateSearchState,
			multiSelectable: false,
		});
	}

	updateSearchState(state: ArticlePickerState) {
		// A dismissed picker hands back an empty selection, so keep the article already chosen.
		this.searchState = {
			...this.searchState,
			...state,
			article: state.articles[0] ?? this.searchState.article,
		};

		// A pre-existing rating-only review is reused: it gets associated with this discussion
		// on save, so start from the rating it already carries.
		const ownReview = this.searchState.article && this.searchState.article.ownReview();

		if (ownReview && ownReview.isSimple()) {
			this.rating(ownReview.rating() ?? 0);
		}

		m.redraw();
	}

	data(): Record<string, unknown> {
		const data = super.data() as DiscussionComposerData;

		const { article } = this.searchState;

		const ownReview = article && article.ownReview();

		data.relationships = data.relationships || {};
		data.relationships.tags = this.composer.fields.tags;
		if (ownReview) {
			data.relationships.review = ownReview;
		}

		// `review` is picked up by GlowingBlue\OrellRezensionen\Listeners\SaveReviewToDatabase.
		return {
			...data,
			review: {
				articleID: article ? article.id() : null,
				articleEAN: article ? article.ean() : null,
				articleTitle: article && article.title(),
				export: this.export(),
				rating: this.rating(),
				shopReviewsLocked: this.exportDisabled() === `${slug}:review_locked`,
			},
		};
	}

	/**
	 * CORE_CODE_OVERRIDE
	 */
	async onsubmit() {
		const original = async () => {
			this.loading = true;

			const data = this.data();

			let discussion: Discussion;

			try {
				const cb = async () => {
					return await app.store.createRecord<Discussion>('discussions').save(data);
				};

				if (this.export() && !this.exportDisabled()) {
					// Cast as in `headerItems()`.
					discussion = (await (Auth.authenticatedCall as (...args: unknown[]) => Promise<unknown>)(
						cb,
						app.translator.trans('glowingblue-orell-rezensionen.forum.user.reviews.export_connect'),
						ReviewListState.gaExportAttrs
					)) as Discussion;
				} else {
					discussion = await cb();
				}
				// The thrown value is a `RequestError` carrying the `gb_internal_code` that
				// handleCustomErrors attaches, but TypeScript only permits `any` or `unknown` here.
			} catch (error: any) {
				this.loaded();

				if (error.gb_internal_code === `${slug}:article_not_found` || error.gb_internal_code === `${slug}:review_locked`) {
					this.exportDisabled(error.gb_internal_code);
					this.export(false);

					// The guard above limits the resolved keys to
					//   glowingblue-orell-rezensionen.forum.composer_review.errors.article_not_found
					//   glowingblue-orell-rezensionen.forum.composer_review.errors.review_locked
					const err_key = error.gb_internal_code.replace(`${slug}:`, '');
					app.alerts.show(app.translator.trans(`glowingblue-orell-rezensionen.forum.composer_review.errors.${err_key}`));

					// Refetch so the composer picks up the article's new availability.
					if (error.gb_internal_code === `${slug}:article_not_found`) {
						this.searchState.article = await app.store.find<Article>('of_articles', this.searchState.article!.id()!);
					}
				} else {
					this.exportDisabled(false);
				}

				throw error;
			}

			if (this.composer.draft) {
				this.composer.draft.delete();

				this.composer.autosaveInterval && clearInterval(this.composer.autosaveInterval);
			}

			this.composer.hide();

			if (app.reviews) {
				app.reviews.refresh();
			}

			// On an article page the new review is inserted into the list in place; elsewhere
			// we navigate to it.
			if (this.attrs.reviewsState) {
				this.attrs.reviewsState.addDiscussion(discussion);
			} else {
				m.route.set(app.route.discussion(discussion));
			}
		};

		const { article } = this.searchState;
		if (!article) {
			app.alerts.show(Alert, { type: 'error' }, app.translator.trans('glowingblue-orell-rezensionen.forum.composer_review.needs_article_message'));

			return;
		}

		if (!this.rating()) {
			app.alerts.show(Alert, { type: 'error' }, app.translator.trans('glowingblue-orell-rezensionen.forum.composer_review.needs_rating_message'));

			return;
		}

		if (!this.composer.fields.content() || this.composer.fields.content().length < MIN_REVIEW_LENGTH) {
			app.alerts.show(Alert, { type: 'error' }, app.translator.trans('glowingblue-orell-rezensionen.forum.composer_review.minimum_char_message'));

			return;

			// TODO : make this same validation in the backend
			// TODO : prevent future modification of post that does not respect this rule
		}

		// Copied from flarum/tags js/src/forum/addTagComposer.js, to make the user pick tags
		// before the review is submitted.
		const chosenTags = this.composer.fields.tags || [];
		const chosenPrimaryTags = chosenTags.filter((tag: Tag) => tag.position() !== null && !tag.isChild());
		const chosenSecondaryTags = chosenTags.filter((tag: Tag) => tag.position() === null);
		// The generated typings declare a required `discussion` parameter the function ignores.
		const selectableTags = (getSelectableTags as () => Tag[])();

		if (
			(!chosenTags.length ||
				chosenPrimaryTags.length < app.forum.attribute<number>('minPrimaryTags') ||
				chosenSecondaryTags.length < app.forum.attribute<number>('minSecondaryTags')) &&
			selectableTags.length
		) {
			// flarum/tags fixes `oninit` to its concrete attrs, which `show()` rejects — same as `manageArticle()`.
			app.modal.show(TagDiscussionModal as unknown as Parameters<typeof app.modal.show>[0], {
				selectedTags: chosenTags,
				onsubmit: async (tags: Tag[]) => {
					this.composer.fields.tags = tags;
					await original();
				},
			});
		} else {
			await original();
		}
	}
}

export default autobind(ReviewComposer);
